import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { router, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, type ComponentType } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ChatLoadError } from '@/components/ChatLoadError';
import { ChatScreenSkeleton } from '@/components/ChatLoadingSkeleton';
import { headerItem } from '@/components/HeaderActions';
import { ChatHeaderTitle, getChatHeaderItems } from '@/components/chat/ChatHeaderContent';
import type { ChatProps } from '@/components/chat/ChatTypes';
import { InquiryGroupConversations } from '@/components/chat/InquiryGroupConversations';
import { useComposerFloats } from '@/components/chat/composer/NativeComposerDock';
import { Header } from '@/components/header';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useInquiryGroupTabs } from '@/hooks/chat/useInquiryGroupTabs';
import { useGroupedInquiriesEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useQuestionGroupChatTabs } from '@/hooks/inbox/useQuestionGroupChatTabs';
import { useAccountSwitcher } from '@/hooks/useAccountSwitcher';
import { useChat } from '@/hooks/useChat';
import type { InquiryParticipant } from '@/hooks/useChat';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { trackEvent } from '@/util/trackEvent';

/**
 * The loaded chat shows the inquiry's "…" menu, so the loading header shows the same item,
 * disabled. The chat is still loading as it is pushed, and a "…" that only appeared once it
 * loaded would pop in after the bar had morphed rather than morph in with it. A chat the inbox
 * already knows is closed has no menu to show, so its loading header holds the slot hidden.
 */
const LOADING_RIGHT_ITEMS = [headerItem.inquiryActions([], false, true)];

const LOADING_CLOSED_RIGHT_ITEMS = [headerItem.inquiryActions([], true, true)];

export function ChatScreenContent({
  ChatContainerComponent,
}: {
  ChatContainerComponent: ComponentType<ChatProps>;
}) {
  const { effectiveUserId } = useAccountSwitcher();

  const { bottom } = useSafeAreaInsets();

  /**
   * The room the loaded chat leaves under its composer, so the skeleton's composer block sits
   * where the composer will. A floating composer rides the safe area alone; an in-flow one also
   * has the screen's bottom margin and, on Android, the extra gap the chat screen adds.
   */
  const skeletonBottomInset = useComposerFloats()
    ? bottom
    : bottom + 4 + (Platform.OS === 'android' ? 20 : 0);

  const { navigate, replace } = useRouter();

  const isCompactLayout = useIsCompactLayout();

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const {
    id,
    name = ' ',
    subject = ' ',
    avatarUrl,
    recipientId,
    tab,
    anonymous,
    support,
    closed,
    onYourHonor,
  } = useLocalSearchParams<{
    id: string;
    name?: string;
    subject?: string;
    avatarUrl?: string;
    recipientId?: string;
    tab?: string;
    anonymous?: string;
    support?: string;
    closed?: string;
    onYourHonor?: string;
  }>();

  const showLoadingAnonymousBadge = anonymous === '1';

  const showLoadingSupportBadge = support === '1';

  const { conversation, recipient, conversationStatus, inquiry, error, retry } = useChat(id);

  const questionGroupTabs = useQuestionGroupChatTabs(id);

  const inquiryGroupRoutes = useInquiryGroupTabs({
    enabled: isInboxGroupedInquiriesEnabled,
    userId: effectiveUserId,
    inquiry: inquiry ?? undefined,
    questionGroupTabs,
  });

  const shouldSyncGroupedSelectionToRoute = isWeb && !isCompactLayout;

  const onSelectGroupedConversation = useCallback(
    (conversationId: string) => {
      const params: { id: string; tab?: string; closed?: string; onYourHonor?: string } = {
        id: conversationId,
        closed: undefined,
        onYourHonor: undefined,
      };

      if (tab) {
        params.tab = tab;
      }

      if (!isWeb) {
        router.setParams(params);

        return;
      }

      if (!shouldSyncGroupedSelectionToRoute) {
        return;
      }

      replace({
        pathname: ROUTES.INBOX_CHAT,
        params,
      });
    },
    [replace, shouldSyncGroupedSelectionToRoute, tab]
  );

  const onViewPlaceholderProfile = useCallback(() => {
    Promise.resolve(trackEvent(ANALYTICS_EVENTS.PROFILE_VIEWED, { source: 'chat_header' })).catch(
      () => undefined
    );

    navigate(`/profile/${recipientId}`);
  }, [navigate, recipientId]);

  if (error) {
    return <ChatLoadError errorType={error.type} onRetry={retry} />;
  }

  if (!effectiveUserId || !conversation || !recipient || !conversationStatus || !inquiry) {
    const placeholderRecipient: Pick<InquiryParticipant, 'id' | 'avatar'> = {
      id: recipientId ? Number(recipientId) : 0,
      avatar: avatarUrl ? { Location: avatarUrl } : null,
    };

    const placeholderHeaderProps = {
      recipient: placeholderRecipient,
      displayName: name,
      displaySubject: subject,
      onViewProfile: onViewPlaceholderProfile,
      showAnonymousBadge: showLoadingAnonymousBadge,
      showSupportBadge: showLoadingSupportBadge,
    };

    return (
      <VStack className="mx-auto w-full flex-1 web:max-w-screen-2xl md:px-10">
        <Header
          isBackButton
          backRoute={ROUTES.INBOX}
          {...(isWeb
            ? { headerTitle: () => <ChatHeaderTitle {...placeholderHeaderProps} /> }
            : {
                leftItems: getChatHeaderItems(placeholderHeaderProps),
                rightItems: closed === '1' ? LOADING_CLOSED_RIGHT_ITEMS : LOADING_RIGHT_ITEMS,
              })}
        />

        <ChatScreenSkeleton
          showComposer={closed !== '1'}
          showActionBar={onYourHonor === '1'}
          bottomInset={skeletonBottomInset}
        />
      </VStack>
    );
  }

  if (inquiryGroupRoutes.length > 1) {
    return (
      <InquiryGroupConversations
        routes={inquiryGroupRoutes}
        tabGrouping={questionGroupTabs ? 'question' : 'send'}
        selectedConversationId={conversation.id}
        userId={effectiveUserId}
        conversation={conversation}
        recipient={recipient}
        conversationStatus={conversationStatus}
        inquiry={inquiry}
        onSelectConversation={onSelectGroupedConversation}
        shouldSyncSelectionToRoute={shouldSyncGroupedSelectionToRoute}
        ChatContainerComponent={ChatContainerComponent}
      />
    );
  }

  return (
    <ChatContainerComponent
      conversationStatus={conversationStatus}
      userId={effectiveUserId}
      conversation={conversation}
      recipient={recipient}
      inquiry={inquiry}
    />
  );
}
